import React, { useState, useEffect, useRef } from "react";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import DropdownMenu from "../DropdownMenu/DropdownMenu";
import "./LoggedInHeaderCategories.css";
import axiosInstance from "../../Config/axiosConfig";

function LoggedInHeaderCategories() {
  const [coursesMenuOpen, setCoursesMenuOpen] = useState(false);
  const [educationLevels, setEducationLevels] = useState([]);
  const [educationClasses, setEducationClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const res = await axiosInstance.post('Public/get-lookups');
        if (res.data.code === 200) {
          // Get education levels (excluding Kindergarten id=1 and College id=5)
          const filteredLevels = (res.data.data.educationLevels || []).filter(
            level => level.id !== 1 && level.id !== 5
          );
          setEducationLevels(filteredLevels);
          
          setEducationClasses(res.data.data.educationClasses || []);
          setSubjects(res.data.data.subjects || []);
        }
      } catch (err) {
        console.error("Error fetching lookups:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLookups();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        const menuButton = document.querySelector('.courses-drop-down');
        if (menuButton && !menuButton.contains(event.target)) {
          setCoursesMenuOpen(false);
        }
      }
    };

    if (coursesMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [coursesMenuOpen]);

  const toggleCoursesMenu = (e) => {
    e.stopPropagation();
    setCoursesMenuOpen(!coursesMenuOpen);
  };

  // Format data for dropdown menu
  const formatEducationLevels = () => {
    const formattedLevels = {};
    
    educationLevels.forEach(level => {
      const levelClasses = educationClasses.filter(
        cls => cls.education_level_id === level.id
      );
      
      // Group subjects by education class for this level
      const classGroups = {};
      levelClasses.forEach(cls => {
        // Get subjects that might be associated with this class
        // Note: You might need to adjust this based on your actual data structure
        const classSubjects = subjects.filter(subject => 
          // This is a placeholder - adjust based on your actual data relationships
          subject.education_class_id === cls.id
        );
        
        if (classSubjects.length > 0) {
          classGroups[cls.name] = classSubjects.map(sub => sub.name);
        } else {
          classGroups[cls.name] = [`${cls.name} Classes`];
        }
      });
      
      // If we have class groups, use them, otherwise use a generic placeholder
      if (Object.keys(classGroups).length > 0) {
        formattedLevels[level.name] = classGroups;
      } else {
        formattedLevels[level.name] = [`${level.name} Classes`];
      }
    });
    
    return formattedLevels;
  };

  const handleNavigateToCourses = (filterData) => {
    console.log("Navigating to courses with filter:", filterData);
    
    // Parse the filter data from the click event
    // filterData could be: "Primary School/Lower Primary/Mathematics"
    const parts = filterData.split('/');
    
    if (parts.length === 1) {
      // Only level selected
      const levelName = parts[0];
      const level = educationLevels.find(l => l.name === levelName);
      if (level) {
        navigate("/courses", { 
          state: { 
            filter: { educationLevelId: level.id }
          }
        });
      }
    } else if (parts.length === 2) {
      // Level and class selected
      const [levelName, className] = parts;
      const level = educationLevels.find(l => l.name === levelName);
      const classItem = educationClasses.find(c => c.name === className);
      
      if (level && classItem) {
        navigate("/courses", { 
          state: { 
            filter: { 
              educationLevelId: level.id,
              educationClass: classItem.id
            }
          }
        });
      }
    } else if (parts.length === 3) {
      // Level, class, and subject selected
      const [levelName, className, subjectName] = parts;
      const level = educationLevels.find(l => l.name === levelName);
      const classItem = educationClasses.find(c => c.name === className);
      const subject = subjects.find(s => s.name === subjectName);
      
      if (level && classItem && subject) {
        navigate("/courses", { 
          state: { 
            filter: { 
              educationLevelId: level.id,
              educationClass: classItem.id,
              subjectId: subject.id
            }
          }
        });
      }
    }
    
    setCoursesMenuOpen(false);
  };

  const handleExploreClick = () => navigate("/user-classes");
  const handleNavigateToExams = () => navigate("/exam-system");

  if (loading) {
    return (
      <div className="header-categories-container">
        <div className="header-categories-row">
          <div className="skeleton-menu-item"></div>
          <div className="skeleton-menu-item"></div>
          <div className="skeleton-menu-item"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="header-categories-container" ref={menuRef}>
      <div className="header-categories-row">
        <div className="courses-drop-down" onClick={toggleCoursesMenu}>
          <span className="courses-categories">Classes</span>
          <DownOutlined className={`down-arrow-courses ${coursesMenuOpen ? "rotate" : ""}`} />
        </div>
        <span className="explore-categories" onClick={handleExploreClick}>
          My Classes
        </span>
        <span className="explore-categories" onClick={handleNavigateToExams}>Exams</span>
      </div>
      
      {coursesMenuOpen && (
        <DropdownMenu 
          educationLevels={formatEducationLevels()} 
          onItemClick={handleNavigateToCourses} 
        />
      )}
    </div>
  );
}

export default LoggedInHeaderCategories;